import {
  type ThrowOnTag,
  valueOrThrow,
} from '@listening/utils/call-api-promise';
import { Cause, Exit } from 'effect';
import { AsyncResult } from 'effect/reactivity';
import {
  type ErrorOf,
  type LoadedHandle,
  Loader,
  type Source,
  useAtomResult,
} from 'raf-effect-atom';
import { keepLatest } from './load-atom';

/**
 * The value a loader waited for with `ensure`. A typed failure listed in
 * `throwOnTag` throws what its handler returns, such as `notFound()`; any
 * other failure throws to the route's error component.
 */
export const successOrThrow = <S extends Source>(
  handle: LoadedHandle<S>,
  throwOnTag?: ThrowOnTag<ErrorOf<S>>,
) => {
  const result = Loader.result(handle);
  return valueOrThrow(
    AsyncResult.isSuccess(result)
      ? Exit.succeed(result.value)
      : Exit.failCause(result.cause),
    throwOnTag,
  );
};

/**
 * The value of a read a loader started, kept through a failed background
 * refresh. A failure with no value to show throws to the route's error
 * component.
 */
export const useLoadedValue = <S extends Source>(
  source: S,
  handle: Parameters<typeof useAtomResult<S>>[1],
) => {
  const result = keepLatest(useAtomResult(source, handle));
  if (AsyncResult.isFailure(result)) throw Cause.squash(result.cause);
  return AsyncResult.getOrThrow(result);
};
